import {
    DataGrid,
    GridColDef,
    GridRenderCellParams,
    GridRenderEditCellParams,
    useGridApiContext,
} from "@mui/x-data-grid";
import { Autocomplete, Box, Chip, TextField, Select, MenuItem } from "@mui/material";
import { useCallback, useState, useEffect } from "react";
import { FlagIcon } from "src/components/iconify/flagIcon";
import Iconify from "src/components/iconify";
import countriesJson from "src/utils/countries.json";

type Props = {
    rows: any[];
    onChange: (rows: any[]) => void;
};

// --- Composant custom pour la plateforme ---
const PlatformEditInputCell = ({
    id,
    value,
    field,
    row,
    onEdit,
}: GridRenderEditCellParams & { onEdit?: (data: Record<string, any>[]) => void }) => {
    const apiRef = useGridApiContext();

    const handleChange = useCallback(
        (_: any, newValue: string[]) => {
            const updatedRow = { ...row, [field]: newValue };
            apiRef.current.setEditCellValue({ id, field, value: newValue });
            onEdit?.([updatedRow]);
        },
        [apiRef, id, field, row, onEdit]
    );

    const currentValues = Array.isArray(value) ? value : [];

    return (
        <Box sx={{ width: "100%", px: 1 }}>
            <Autocomplete
                multiple
                disableClearable
                disableCloseOnSelect
                options={["Google", "Bing"]}
                value={currentValues}
                onChange={handleChange}
                size="small"
                fullWidth
                renderTags={(tagValue) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, pl: 0.5 }}>
                        {tagValue.map((option, index) => (
                            <Chip
                                key={index}
                                label={option}
                                size="small"
                                sx={{
                                    fontWeight: 500,
                                    height: 22,
                                    "& .MuiChip-label": { px: 1.2 },
                                }}
                            />
                        ))}
                    </Box>
                )}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        variant="outlined"
                        size="small"
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                p: "2px 6px",
                                alignItems: "center",
                            },
                        }}
                    />
                )}
            />
        </Box>
    );
};

export default function DataGridScrapingDetails({ rows, onChange }: Props) {
    const [localRows, setLocalRows] = useState<any[]>([]);

    useEffect(() => {
        if (rows && rows.length > 0) {
            const formatted = rows.map((r, i) => ({ id: r.id ?? i, ...r }));
            setLocalRows(formatted);
        }
    }, [rows]);

    // 🔥 corrige ici : on propage les modifs au parent à chaque changement
    const handleEditCellChange = useCallback(
        (id: any, field: string, value: any) => {
            setLocalRows((prev) => {
                const updated = prev.map((r) =>
                    r.id === id ? { ...r, [field]: value } : r
                );
                onChange(updated); // ✅ on renvoie les nouvelles valeurs au parent
                return updated;
            });
        },
        [onChange]
    );

    const columns: GridColDef[] = [
        {
            field: "platform_scraping",
            headerName: "Platform",
            flex: 1,
            minWidth: 200,
            editable: true,
            renderEditCell: (params) => (
                <PlatformEditInputCell
                    {...params}
                    onEdit={(rows) => {
                        const updatedRow = rows[0];
                        handleEditCellChange(
                            updatedRow.id,
                            params.field,
                            updatedRow[params.field]
                        );
                    }}
                />
            ),
            renderCell: (params: GridRenderCellParams) => {
                const value = Array.isArray(params.value) ? params.value : [];
                return value.length > 0 ? (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {value.map((v: string) => (
                            <Chip key={v} label={v} size="small" sx={{ fontWeight: 500 }} />
                        ))}
                    </Box>
                ) : (
                    <span style={{ opacity: 0.6 }}>--</span>
                );
            },
        },
        { field: "keyword", headerName: "Keyword", flex: 1, minWidth: 150 },
        {
            field: "country",
            headerName: "Country",
            flex: 1,
            minWidth: 180,
            renderCell: ({ value }) => {
                const countries = Array.isArray(value) ? value : [value].filter(Boolean);

                return (
                    <Box display="flex" flexWrap="wrap" gap={1}>
                        {countries.length > 0 &&
                            countries.map((country: string, i: number) => {
                                const flagCode =
                                    countriesJson.find((c: any) => c.name === country)?.code?.toLowerCase();

                                return (
                                    <Box
                                        key={i}
                                        display="flex"
                                        alignItems="center"
                                        gap={0.6}
                                        sx={{ whiteSpace: "nowrap" }}
                                    >
                                        {flagCode ? (
                                            <FlagIcon
                                                isCircle={false}
                                                code={flagCode}
                                                sx={{ width: 18, height: 18, borderRadius: "3px" }}
                                            />
                                        ) : (
                                            <Iconify
                                                icon="flat-color-icons:globe"
                                                sx={{ width: 20, height: 20 }}
                                            />
                                        )}
                                        <span>{country}</span>
                                    </Box>
                                );
                            })
                        }
                    </Box>
                );
            },
        },

        {
            field: "frequency",
            headerName: "Frequency",
            flex: 1,
            minWidth: 140,
            editable: true,
            renderEditCell: (params) => {
                const { id, field, value } = params;
                return (
                    <Select
                        autoFocus
                        fullWidth
                        size="small"
                        value={value || "60 min"}
                        onChange={(e) => {
                            const newValue = e.target.value;
                            params.api.setEditCellValue({ id, field, value: newValue }, e);
                            // ✅ quitte le mode édition proprement
                            params.api.stopCellEditMode({ id, field });
                        }}
                    >
                        {["5 min", "10 min", "30 min", "60 min", "2 hours", "4 hours", "8 hours", "daily"].map((opt) => (
                            <MenuItem key={opt} value={opt}>
                                {opt}
                            </MenuItem>
                        ))}
                    </Select>
                );
            },
            renderCell: (params) => params.value || "--",
        },

        {
            field: "device",
            headerName: "Device",
            flex: 1,
            minWidth: 150,
            renderCell: (params) =>
                Array.isArray(params.value)
                    ? params.value.join(", ")
                    : params.value || "--",
        },
    ];

    return (
        <Box mt={2} sx={{ height: 200, width: "100%" }}>
            <DataGrid
                rows={localRows}
                columns={columns}
                disableRowSelectionOnClick
                editMode="cell"
                processRowUpdate={(newRow) => {
                    setLocalRows((prev) => {
                        const updated = prev.map((r) => (r.id === newRow.id ? newRow : r));
                        onChange(updated); // <-- renvoie bien le state mis à jour
                        return updated;
                    });
                    return newRow;
                }}


                hideFooter
                getRowId={(r) => r.id}
            />
        </Box>
    );
}
